import { AuthenticatedHttpClient } from "@/lib/axios";

/* eslint-disable @typescript-eslint/no-explicit-any */
export class SSEService {
  private http: AuthenticatedHttpClient;
  private eventSources: Map<string, EventSource> = new Map();

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  // Connect to SSE stream for a specific test
  async connectToTestStream(
    testId: string,
    callbacks: {
      onMessage?: (data: any) => void;
      onOpen?: () => void;
      onError?: (error: Event) => void;
      onClose?: () => void;
    },
  ) {
    // Close existing connection if any
    this.disconnectFromTestStream(testId);

    try {
      // First make an authenticated request to get a session cookie
      await this.http.get(`/inbox-placement/auth-sse/${testId}`);

      // Create new connection - the cookie from previous request will be sent automatically
      const eventSource = new EventSource(
        `${import.meta.env.VITE_API_URL}inbox-placement/test-stream/${testId}`,
      );

      // Set up event handlers
      eventSource.onmessage = (event) => {
        if (callbacks.onMessage) {
          try {
            const data = JSON.parse(event.data);
            callbacks.onMessage(data);
          } catch (error) {
            console.error("Error parsing SSE message:", error);
          }
        }
      };

      eventSource.onopen = () => {
        if (callbacks.onOpen) {
          callbacks.onOpen();
        }
      };

      eventSource.onerror = (error) => {
        if (callbacks.onError) {
          callbacks.onError(error);
        }

        // Close connection on error and try to reconnect after a delay
        this.disconnectFromTestStream(testId);

        setTimeout(async () => {
          await this.connectToTestStream(testId, callbacks);
        }, 5000); // Retry after 5 seconds
      };

      // Store the connection
      this.eventSources.set(testId, eventSource);
    } catch (error) {
      console.error("Error connecting to test stream:", error);
      if (callbacks.onError) {
        callbacks.onError(new Event("error"));
      }
    }
  }

  // Disconnect from a specific test stream
  disconnectFromTestStream(testId: string): void {
    const eventSource = this.eventSources.get(testId);

    if (eventSource) {
      eventSource.close();
      this.eventSources.delete(testId);
    }
  }

  // Disconnect from all streams
  disconnectAll(): void {
    this.eventSources.forEach((eventSource) => {
      eventSource.close();
    });

    this.eventSources.clear();
  }

  // Fetch previous updates for a test
  async fetchTestUpdates(testId: string) {
    try {
      const response = await this.http.get(
        `/inbox-placement/test-progress/${testId}`,
      );
      return response as unknown as any;
    } catch (error) {
      console.error("Error fetching test updates:", error);
      return [];
    }
  }
}
